import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { Logger } from '@saviynt/common';
import {
  ApplicationIcon,
  Box,
  Icon,
  Illustration,
  Loader,
  TabContent,
  TabGroup,
  TabItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@saviynt/design-system';

import { useLazyGetCustomListQuery } from '../../../../features/ChatModule';
import {
  APPLICATION_WORKFLOW_KEYS,
  CF_CRT_MED,
  CF_NEU_BLD,
  CF_SEC_BLD,
  LIMIT_INLINE_COUNT,
  MIN_COMMENT_LENGTH,
  testIds,
} from '../../../constants';
import { useEffectAfterMount, useInViewObserver } from '../../../hooks';
import { WRAPPER_MODE } from '../../UiWrapper';
import {
  ACCESS_TYPE_FILTER_VALUE,
  DEFAULT_ACCESS_FILTERS,
  ENTITLEMENT_TYPE_FILTER,
  ENTITLEMENT_TYPE_FILTER_VALUE,
  MAX_COUNT,
} from '../constants';
import {
  ListControls,
  type ListFilters,
  ListSidePanel,
  ListSkeleton,
  SoxCritical,
} from '../Shared';
import {
  type AccessConfigMap,
  type AccessListResponse,
  AccessTypeMap,
  type ApplicationRoleAccess,
  type CustomListModalTypes,
  type EndpointParams,
  type EnterpriseRoleAccess,
  type EnterpriseRoleAccessData,
  type EntitlementAccess,
  type EntitlementAccessData,
  type RequestOptionConfig,
} from '../types';
import { groupByEntitlementType } from '../utils';

import AccessModalListItem from './AccessModalListItem';

import './AccessModal.css';

/**
 * An access list item augmented with selection and validation tracking state.
 */
export type TrackedListItem = {
  tracker: {
    id: number;
    type: AccessTypeMap;
    isChecked: boolean;
    // New block
    isStartDate: boolean;
    isEndDate: boolean;
    isStartDateMandatory: boolean;
    isEndDateMandatory: boolean;
    allowedEndDateRange?: string | null;
    currentStartDate?: Date | null;
    currentEndDate?: Date | null;
    isStartDateDisabled?: boolean;
    // New block
    isStartDateCritical: boolean;
    isEndDateCritical: boolean;
    currentJustification: string;
    isJustificationRequired: boolean;
    isJustificationCritical?: boolean;
  };
} & AccessListResponse;

interface AccessModalSegmentProps {
  isFullView: boolean;
  isDisabled: boolean;
  assignedCount: number | null;
  groupName: AccessTypeMap;
  list: TrackedListItem[];
  isLast: boolean;
  handleClick: (id: number, type: AccessTypeMap, config: object) => void;
  handleRowClick: (id: number, type: AccessTypeMap) => void;
}

const AccessModalSegment = React.memo(function AccessModalSegment({
  isFullView,
  isDisabled,
  assignedCount,
  groupName,
  list,
  isLast,
  handleClick,
  handleRowClick,
}: AccessModalSegmentProps) {
  return (
    <>
      <Box tag='div' className='AccessModalSegment'>
        <Typography kind='h3' color={CF_NEU_BLD}>
          {groupName === AccessTypeMap.entitlement && (
            <FormattedMessage
              id='e8hsVA'
              defaultMessage='Entitlement'
              description='Header for entitlement section in AccessModal component'
            />
          )}
          {groupName === AccessTypeMap.enterpriseRole && (
            <FormattedMessage
              id='BL/RI2'
              defaultMessage='Enterprise Role'
              description='Header for enterprise role section in AccessModal component'
            />
          )}
          {groupName === AccessTypeMap.applicationRole && (
            <FormattedMessage
              id='NTYbXF'
              defaultMessage='Application Role'
              description='Header for application role section in AccessModal component'
            />
          )}
        </Typography>
        <Box tag='div' className='AccessModalSegment-listWrapper'>
          {list.map((item) => (
            <AccessModalListItem
              key={`${item.tracker.id}-${item.tracker.type}`}
              isFullView={isFullView}
              isDisabled={isDisabled}
              assignedCount={assignedCount}
              isAssignedList={false}
              listItem={item}
              handleClick={handleClick}
              handleRowClick={handleRowClick}
              handleOptionClick={() => {}}
              handleComment={() => {}}
            />
          ))}
        </Box>
      </Box>
      {!isLast && <hr />}
    </>
  );
});

const DEFAULT_SIDEPANEL_STATE = {
  activeIndex: null,
  activeItem: null,
};

const DEFAULT_LIST_STATE = {
  currentOffset: 0,
  totalCount: null as number | null,
  isObserverInView: true,
  isListError: false,
  isListLoading: false,
};

const TYPE_SORT_ORDER: Record<AccessTypeMap, number> = {
  [AccessTypeMap.entitlement]: 0,
  [AccessTypeMap.enterpriseRole]: 1,
  [AccessTypeMap.applicationRole]: 2,
};

interface ChildAccess {
  id: string;
  displayName: string | null;
  name?: string;
}

interface AccessModalProps {
  isFullView: boolean;
  workflowType: string;
  retentions?: AccessListResponse[];
  fetchList: (pagination?: EndpointParams) => Promise<unknown>;
  setChecked: React.Dispatch<React.SetStateAction<CustomListModalTypes[]>>;
  currentMode: string;
  setInlineItemCount: React.Dispatch<React.SetStateAction<number | null>>;
}

/**
 * Imperative handle exposed by AccessModal via ref.
 */
export interface AccessModalHandle {
  validate: () => boolean;
  isEmpty: () => boolean;
}

/**
 * Modal for selecting and managing access items (entitlements, enterprise roles,
 * and application roles). Supports search, filtering, date/justification validation,
 * and a detail side panel in full-view mode.
 *
 * Exposes `validate` and `isEmpty` methods via ref.
 *
 * @param props - {@link AccessModalProps}
 * @param props.isFullView - Whether the modal renders in expanded (full) layout
 * @param props.workflowType - Current application workflow key
 * @param props.retentions - Pre-selected access items to retain
 * @param props.fetchList - Fetches the available access list from the API
 * @param props.setChecked - Callback to propagate checked selections upstream
 * @param props.currentMode - Current UI wrapper mode (inline or modal)
 * @param props.setInlineItemCount - Callback to set the total item count when inline
 * @param ref - React ref exposing {@link AccessModalHandle}
 * @returns The access selection modal UI
 */
const AccessModal = forwardRef<AccessModalHandle, AccessModalProps>(
  (
    {
      isFullView,
      workflowType,
      retentions,
      fetchList,
      setChecked,
      currentMode,
      setInlineItemCount,
    },
    ref
  ) => {
    const [listControls, setListControls] = useState({
      searchText: '',
      controls: DEFAULT_ACCESS_FILTERS as ListFilters[],
    });
    const [assignType, setAssignType] = useState(1);
    const [listState, setListState] = useState(DEFAULT_LIST_STATE);
    const [listRows, setListRows] = useState<TrackedListItem[]>([]);
    const [accessConfig, setAccessConfig] = useState<AccessConfigMap | null>(
      null
    );
    const [roleAccessConfig, setRoleAccessConfig] = useState<{
      [AccessTypeMap.enterpriseRole]?: RequestOptionConfig;
      [AccessTypeMap.applicationRole]?: RequestOptionConfig;
    } | null>(null);
    const [recommendedMode, setRecommendedMode] = useState(1);
    const [activeRows, setActiveRows] = useState<TrackedListItem[]>([]);
    const [delta, setDelta] = useState<{
      added: TrackedListItem[];
      removed: TrackedListItem[];
    }>({
      added: (retentions ?? []).map((item) => ({
        ...item,
        tracker: {
          isChecked: true,
        },
      })) as unknown as TrackedListItem[],
      removed: [] as TrackedListItem[],
    });

    const listRowsRef = useRef(listRows);
    const activeRowsRef = useRef(activeRows);
    const deltaRef = useRef(delta);
    const assignTypeRef = useRef(assignType);

    useEffect(() => {
      listRowsRef.current = listRows;
    }, [listRows]);

    useEffect(() => {
      activeRowsRef.current = activeRows;
    }, [activeRows]);

    useEffect(() => {
      deltaRef.current = delta;
    }, [delta]);

    useEffect(() => {
      assignTypeRef.current = assignType;
    }, [assignType]);

    // SidePanel
    const focusedList = useMemo(() => {
      if (assignType === 2) {
        return activeRows;
      }

      return listRows;
    }, [activeRows, assignType, listRows]);
    const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
    const [sidePanelTab, setSidePanelTab] = useState('1');
    const [sidePanelContext, setSidePanelContext] = useState<{
      activeIndex: number | null;
      activeItem: TrackedListItem | null;
    }>(DEFAULT_SIDEPANEL_STATE);
    const [sidePanelState, setSidePanelState] = useState({
      isAccessLoading: true,
      isAccessError: false,
      children: [] as ChildAccess[],
    });

    const [fetchAccessMetaData] = useLazyGetCustomListQuery();

    const handleFetchAccessMetaData = useCallback(
      async (data: { entitlementValueKey?: string; roleId?: string }) => {
        try {
          const response = await fetchAccessMetaData({
            url: '/api/v5/requestmcp/getAccessMetadata',
            data,
          }).unwrap();

          return response;
        } catch (error) {
          throw Error(JSON.stringify(error));
        }
      },
      [fetchAccessMetaData]
    );

    // Constants
    const isValidApiCall = useMemo(
      () =>
        Boolean(
          !listState.isListLoading &&
            (listState.totalCount === null ||
              (listState.currentOffset < listState.totalCount &&
                listRows.length < listState.totalCount))
        ),
      [
        listState.currentOffset,
        listState.totalCount,
        listRows.length,
        listState.isListLoading,
      ]
    );
    const isFilterApplied = useMemo(
      () =>
        listControls.searchText.trim().length > 0 ||
        listControls.controls
          .find((f) => f.value === ACCESS_TYPE_FILTER_VALUE)
          ?.options.some((o) => o.isChecked),
      [listControls.searchText, listControls.controls]
    );

    // List management
    const listObserverElement = useInViewObserver(({ isInView }) => {
      setListState((prev) => ({ ...prev, isObserverInView: isInView }));
    });

    useEffect(() => {
      if (!listState.isObserverInView || !isValidApiCall) {
        return;
      }

      Logger.log(
        'Fetching',
        listState.currentOffset,
        'to',
        listState.currentOffset + MAX_COUNT
      );

      setListState((prev) => ({
        ...prev,
        isListLoading: true,
        isListError: false,
      }));

      const currentAssignType = assignTypeRef.current;
      const accessTypeFilterValue = listControls.controls
        .find((f) => f.value === ACCESS_TYPE_FILTER_VALUE)
        ?.options.find((o) => o.isChecked)?.value;
      const entTypeFilterValue = listControls.controls
        .find((f) => f.value === ENTITLEMENT_TYPE_FILTER_VALUE)
        ?.options.find((o) => o.isChecked)?.value;

      const getFilterType = () => {
        if (currentAssignType !== 1) {
          return undefined;
        }

        if (
          accessTypeFilterValue === AccessTypeMap.entitlement &&
          entTypeFilterValue
        ) {
          return ENTITLEMENT_TYPE_FILTER_VALUE;
        }

        return accessTypeFilterValue;
      };

      const getEntitlementTypeFilter = () => {
        if (currentAssignType !== 1) {
          return undefined;
        }

        if (
          accessTypeFilterValue === AccessTypeMap.entitlement &&
          entTypeFilterValue
        ) {
          return entTypeFilterValue;
        }

        return undefined;
      };

      const payload = {
        max: MAX_COUNT,
        offset: listState.currentOffset,
        payload: {
          status: currentAssignType === 1 ? 'Available' : 'Assigned',
          wsearch:
            currentAssignType === 1 ? listControls.searchText : undefined,
          filterType: getFilterType(),
          entitlementType: getEntitlementTypeFilter(),
        },
      };

      interface ApiResponse {
        [AccessTypeMap.entitlement]?: EntitlementAccess;
        [AccessTypeMap.enterpriseRole]?: EnterpriseRoleAccess;
        [AccessTypeMap.applicationRole]?: ApplicationRoleAccess;
        success: boolean;
        grandTotal: number;
      }

      fetchList(payload)
        .then((response) => {
          const { ENTITLEMENT, ENTERPRISE_ROLE, APPLICATION_ROLE, success } =
            response as ApiResponse;

          const totalRecords =
            (ENTITLEMENT?.totalRecords ?? 0) +
            (ENTERPRISE_ROLE?.totalRecords ?? 0) +
            (APPLICATION_ROLE?.totalRecords ?? 0);

          if (!success) {
            throw Error('Something went wrong');
          }

          if (
            totalRecords > LIMIT_INLINE_COUNT &&
            currentMode === WRAPPER_MODE.INLINE
          ) {
            setInlineItemCount(totalRecords);
          }

          const typedAccessConfig: AccessConfigMap = {
            [AccessTypeMap.entitlement]: ENTITLEMENT?.entitlementTypeConfigs,
            [AccessTypeMap.enterpriseRole]:
              ENTERPRISE_ROLE?.enterpriseRoleDateConfig,
            [AccessTypeMap.applicationRole]:
              APPLICATION_ROLE?.applicationRoleDateConfig,
          };

          const trackedResponse: TrackedListItem[] = [
            ...(ENTITLEMENT?.data.map((e) => {
              // Date configuration
              const entitlementConfig =
                typedAccessConfig[AccessTypeMap.entitlement]?.[
                  e.entitlementtype
                ];

              const isStartDate = Boolean(entitlementConfig?.isStartDate);
              const isEndDate = Boolean(entitlementConfig?.isEndDate);
              const isStartDateMandatory = Boolean(
                entitlementConfig?.isStartDateMandatory
              );
              const isEndDateMandatory = Boolean(
                entitlementConfig?.isEndDateMandatory
              );
              const currentStartDate =
                e.startdate && e.startdate.trim().length > 0
                  ? new Date(e.startdate)
                  : new Date();
              let currentEndDate =
                e.enddate && e.enddate.trim().length > 0
                  ? new Date(e.enddate)
                  : null;

              // Apply default time frame logic if conditions are met
              if (
                isStartDate &&
                isEndDate &&
                entitlementConfig &&
                entitlementConfig.defaultTimeFrameHrs !== null &&
                currentEndDate !== null &&
                (currentStartDate.getTime() - Date.now()) /
                  (1000 * 60 * 60 * 24) <
                  Number(entitlementConfig.defaultTimeFrameHrs) / 24
              ) {
                const today = new Date();

                currentEndDate = new Date(
                  today.getTime() +
                    Number(entitlementConfig.defaultTimeFrameHrs) *
                      60 *
                      60 *
                      1000
                );
              }

              // Justification configuration
              const isJustificationRequired = Boolean(
                e.mandatoryBusinessJustification === true &&
                  (e.soxcritical === 'CRITICAL' ||
                    e.soxcritical === 'VERY_HIGH')
              );

              return {
                ...e,
                tracker: {
                  id: e.entitlement_valuekey,
                  type: AccessTypeMap.entitlement,
                  isChecked:
                    currentAssignType === 1 ? e.isChecked ?? false : true,
                  isStartDate,
                  isEndDate,
                  isStartDateMandatory,
                  isEndDateMandatory,
                  isStartDateCritical: false,
                  isEndDateCritical: false,
                  currentStartDate,
                  currentEndDate,
                  allowedEndDateRange: entitlementConfig?.maxTimeFrameHrs,
                  isStartDateDisabled: e.isStartDateDisabled,
                  isJustificationRequired,
                  isJustificationCritical: false,
                  currentJustification: e.justification ?? '',
                },
              };
            }) ?? []),
            ...(ENTERPRISE_ROLE?.data.map((e) => {
              // Date configuration
              const roleConfig =
                typedAccessConfig[AccessTypeMap.enterpriseRole];
              const isStartDate = Boolean(roleConfig?.isStartDate);
              const isEndDate = Boolean(roleConfig?.isEndDate);
              const isStartDateMandatory = Boolean(
                roleConfig?.isStartDateMandatory
              );
              const isEndDateMandatory = Boolean(
                roleConfig?.isEndDateMandatory
              );
              const currentStartDate =
                e.startdate && e.startdate.trim().length > 0
                  ? new Date(e.startdate)
                  : new Date();
              const currentEndDate =
                e.enddate && e.enddate.trim().length > 0
                  ? new Date(e.enddate)
                  : null;
              // Justification configuration
              const isJustificationRequired = Boolean(
                e.mandatoryBusinessJustification === true &&
                  (e.soxcritical === 'CRITICAL' ||
                    e.soxcritical === 'VERY_HIGH')
              );

              return {
                ...e,
                tracker: {
                  id: e.roleid,
                  type: AccessTypeMap.enterpriseRole,
                  isChecked:
                    currentAssignType === 1 ? e.isChecked ?? false : true,
                  isStartDate,
                  isEndDate,
                  isStartDateMandatory,
                  isEndDateMandatory,
                  isStartDateCritical: false,
                  isEndDateCritical: false,
                  currentStartDate,
                  currentEndDate,
                  isStartDateDisabled: e.isStartDateDisabled,
                  isJustificationCritical: false,
                  isJustificationRequired,
                  currentJustification: e.justification ?? '',
                },
              };
            }) ?? []),
            ...(APPLICATION_ROLE?.data.map((e) => {
              // Date configuration
              const roleConfig =
                typedAccessConfig[AccessTypeMap.applicationRole];
              const isStartDate = Boolean(roleConfig?.isStartDate);
              const isEndDate = Boolean(roleConfig?.isEndDate);
              const isStartDateMandatory = Boolean(
                roleConfig?.isStartDateMandatory
              );
              const isEndDateMandatory = Boolean(
                roleConfig?.isEndDateMandatory
              );
              const currentStartDate =
                e.startdate && e.startdate.trim().length > 0
                  ? new Date(e.startdate)
                  : new Date();
              const currentEndDate =
                e.enddate && e.enddate.trim().length > 0
                  ? new Date(e.enddate)
                  : null;

              // Justification configuration
              const isJustificationRequired = Boolean(
                e.mandatoryBusinessJustification === true &&
                  (e.soxcritical === 'CRITICAL' ||
                    e.soxcritical === 'VERY_HIGH')
              );

              return {
                ...e,
                tracker: {
                  id: e.roleid,
                  type: AccessTypeMap.applicationRole,
                  isChecked:
                    currentAssignType === 1 ? e.isChecked ?? false : true,
                  isStartDate,
                  isEndDate,
                  isStartDateMandatory,
                  isEndDateMandatory,
                  isStartDateCritical: false,
                  isEndDateCritical: false,
                  currentStartDate,
                  currentEndDate,
                  isStartDateDisabled: e.isStartDateDisabled,
                  isJustificationCritical: false,
                  isJustificationRequired,
                  currentJustification: e.justification ?? '',
                },
              };
            }) ?? []),
          ];

          if (currentAssignType === 1) {
            const currentDelta = deltaRef.current;
            const deltaApplied = trackedResponse.map((item) => {
              const inAdded = currentDelta.added.some(
                (a) =>
                  a.tracker.id === item.tracker.id &&
                  a.tracker.type === item.tracker.type
              );
              const inRemoved = currentDelta.removed.some(
                (r) =>
                  r.tracker.id === item.tracker.id &&
                  r.tracker.type === item.tracker.type
              );

              if (inAdded) {
                return {
                  ...item,
                  tracker: { ...item.tracker, isChecked: true },
                };
              }

              if (inRemoved) {
                return {
                  ...item,
                  tracker: { ...item.tracker, isChecked: false },
                };
              }

              return item;
            });

            setListRows((prev) => [...prev, ...deltaApplied]);
          } else {
            const currentDelta = deltaRef.current;
            let updatedActiveRows = [...trackedResponse];

            currentDelta.added.forEach((addedItem) => {
              const exists = updatedActiveRows.some(
                (r) =>
                  r.tracker.id === addedItem.tracker.id &&
                  r.tracker.type === addedItem.tracker.type
              );

              if (!exists) {
                updatedActiveRows.push(addedItem);
              }
            });

            updatedActiveRows = updatedActiveRows
              .filter(
                (r) =>
                  !currentDelta.removed.some(
                    (rem) =>
                      rem.tracker.id === r.tracker.id &&
                      rem.tracker.type === r.tracker.type
                  )
              )
              .sort((a, b) => {
                const typeOrder =
                  TYPE_SORT_ORDER[a.tracker.type] -
                  TYPE_SORT_ORDER[b.tracker.type];

                if (typeOrder !== 0) {
                  return typeOrder;
                }

                return a.tracker.id - b.tracker.id;
              });

            setActiveRows(updatedActiveRows);
          }

          setListState((prev) => ({
            ...prev,
            totalCount: totalRecords,
            currentOffset: prev.currentOffset + MAX_COUNT,
          }));
          setAccessConfig((prev) => {
            if (prev === null) {
              return typedAccessConfig;
            }

            return {
              ...prev,
              [AccessTypeMap.entitlement]: {
                ...prev[AccessTypeMap.entitlement],
                ...typedAccessConfig[AccessTypeMap.entitlement],
              },
              [AccessTypeMap.enterpriseRole]:
                typedAccessConfig[AccessTypeMap.enterpriseRole] ??
                prev[AccessTypeMap.enterpriseRole],
              [AccessTypeMap.applicationRole]:
                typedAccessConfig[AccessTypeMap.applicationRole] ??
                prev[AccessTypeMap.applicationRole],
            };
          });
          setRoleAccessConfig((prev) => {
            if (prev !== null) {
              return prev;
            }

            return {
              [AccessTypeMap.enterpriseRole]:
                ENTERPRISE_ROLE?.roleRequestOptionConfig,
              [AccessTypeMap.applicationRole]:
                APPLICATION_ROLE?.roleRequestOptionConfig,
            };
          });
        })
        .catch((error) => {
          Logger.log('Received error while fetching: ', error);

          setListState((prev) => ({
            ...prev,
            isListError: true,
          }));
        })
        .finally(() => {
          setListState((prev) => ({
            ...prev,
            isListLoading: false,
            isObserverInView: false,
          }));
        });
    }, [
      isValidApiCall,
      fetchList,
      isFullView,
      listState.isObserverInView,
      listState.currentOffset,
      listControls.searchText,
      listControls.controls,
      currentMode,
      setInlineItemCount,
    ]);

    // Resetting list and offset to default so that observer is visible
    useEffectAfterMount(() => {
      const debounceTimer = setTimeout(() => {
        setListState(DEFAULT_LIST_STATE);
        setListRows([]);
      }, 20);

      return () => clearTimeout(debounceTimer);
    }, [listControls.searchText, listControls.controls]);

    // Resetting list and offset to default so that observer is visible
    useEffectAfterMount(() => {
      setListState(DEFAULT_LIST_STATE);

      if (assignType === 1) {
        setListRows([]);

        return;
      }

      setActiveRows([]);
    }, [assignType]);

    // Sync activeRows and listRows whenever delta changes
    useEffect(() => {
      setActiveRows((prev) => {
        let updated = [...prev];

        delta.added.forEach((addedItem) => {
          const exists = updated.some(
            (r) =>
              r.tracker.id === addedItem.tracker.id &&
              r.tracker.type === addedItem.tracker.type
          );

          if (!exists) {
            updated.push({
              ...addedItem,
              tracker: { ...addedItem.tracker, isChecked: true },
            });
          }
        });

        updated = updated.filter(
          (r) =>
            !delta.removed.some(
              (rem) =>
                rem.tracker.id === r.tracker.id &&
                rem.tracker.type === r.tracker.type
            )
        );

        return updated.sort((a, b) => {
          const typeOrder =
            TYPE_SORT_ORDER[a.tracker.type] - TYPE_SORT_ORDER[b.tracker.type];

          if (typeOrder !== 0) {
            return typeOrder;
          }

          return a.tracker.id - b.tracker.id;
        });
      });

      setListRows((prev) => {
        const updated = prev.map((item) => {
          const inAdded = delta.added.some(
            (a) =>
              a.tracker.id === item.tracker.id &&
              a.tracker.type === item.tracker.type
          );
          const inRemoved = delta.removed.some(
            (r) =>
              r.tracker.id === item.tracker.id &&
              r.tracker.type === item.tracker.type
          );

          if (inAdded && !item.tracker.isChecked) {
            return { ...item, tracker: { ...item.tracker, isChecked: true } };
          }

          if (inRemoved && item.tracker.isChecked) {
            return { ...item, tracker: { ...item.tracker, isChecked: false } };
          }

          return item;
        });

        return updated.some((item, i) => item !== prev[i]) ? updated : prev;
      });
    }, [delta]);

    useEffect(() => {
      setChecked(
        activeRows.map(({ tracker, ...rest }) => {
          const fillerObj: {
            startdate?: string | null;
            enddate?: string | null;
            justification?: string;
          } = {};

          // Adding dates
          if (tracker.currentStartDate) {
            fillerObj.startdate = tracker.currentStartDate.toISOString();
          } else {
            fillerObj.startdate = null;
          }

          if (tracker.currentEndDate) {
            fillerObj.enddate = tracker.currentEndDate.toISOString();
          } else {
            fillerObj.enddate = null;
          }

          // Adding justification
          if (tracker.isJustificationRequired) {
            fillerObj.justification = tracker.currentJustification;
          }

          return {
            ...rest,
            ...fillerObj,
            isChecked: true,
            type: tracker.type,
          };
        })
      );
    }, [activeRows, setChecked, assignType]);

    // Sidepanel call
    useEffect(() => {
      if (isFullView && sidePanelTab === '2' && sidePanelContext.activeItem) {
        setSidePanelState((prev) => ({
          ...prev,
          isAccessError: false,
          isAccessLoading: true,
        }));

        const data =
          sidePanelContext.activeItem.tracker.type === AccessTypeMap.entitlement
            ? {
                entitlementValueKey: String(
                  sidePanelContext.activeItem.tracker.id
                ),
              }
            : { roleId: String(sidePanelContext.activeItem.tracker.id) };

        handleFetchAccessMetaData(data)
          .then((response) => {
            const typedResponse = response as {
              success: boolean;
              childAccess: ChildAccess[];
            };

            if (!typedResponse.success) {
              throw Error('Failed to get success response for access metadata');
            }

            setSidePanelState((prev) => ({
              ...prev,
              children: typedResponse.childAccess,
            }));
          })
          .catch(() => {
            Logger.log('Failed to get child entities');

            setSidePanelState((prev) => ({
              ...prev,
              isAccessError: true,
            }));
          })
          .finally(() => {
            setSidePanelState((prev) => ({
              ...prev,
              isAccessLoading: false,
            }));
          });
      }
    }, [handleFetchAccessMetaData, sidePanelContext, isFullView, sidePanelTab]);

    // Utils
    const handleClick = useCallback(
      (id: number, type: AccessTypeMap, config: object) => {
        let itemConfig: number | null = null;

        if (type === AccessTypeMap.entitlement) {
          const entType = (config as { entitlementType: string })
            .entitlementType;

          itemConfig = Number(
            accessConfig
              ? accessConfig[AccessTypeMap.entitlement]?.[entType]
                  ?.entitlementTypeRequestOptionConfig?.key
              : null
          );
        }

        if (type === AccessTypeMap.enterpriseRole) {
          itemConfig = Number(
            roleAccessConfig
              ? roleAccessConfig[AccessTypeMap.enterpriseRole]?.key
              : null
          );
        }

        if (type === AccessTypeMap.applicationRole) {
          itemConfig = Number(
            roleAccessConfig
              ? roleAccessConfig[AccessTypeMap.applicationRole]?.key
              : null
          );
        }

        const isSingleSelect = itemConfig === 1;

        // if (itemConfig !== 1) {
        //   setListRows((prev) =>
        //     prev.map((item) => {
        //       if (item.tracker.id === id && item.tracker.type === type) {
        //         return {
        //           ...item,
        //           tracker: {
        //             ...item.tracker,
        //             isChecked: !item.tracker.isChecked,
        //             ...(item.tracker.isStartDateDisabled
        //               ? {}
        //               : {
        //                   currentStartDate: item.tracker.isChecked
        //                     ? null
        //                     : item.tracker.currentStartDate,
        //                 }),
        //             currentEndDate: item.tracker.isChecked
        //               ? null
        //               : item.tracker.currentEndDate,
        //             isStartDateCritical: false,
        //             isEndDateCritical: false,
        //             isJustificationCritical: false,
        //             currentJustification: '',
        //           },
        //         };
        //       }

        //       return item;
        //     })
        //   );

        //   return;
        // }

        const clickedItem =
          listRowsRef.current.find(
            (item) => item.tracker.id === id && item.tracker.type === type
          ) ??
          activeRowsRef.current.find(
            (item) => item.tracker.id === id && item.tracker.type === type
          );

        const wasChecked = clickedItem?.tracker.isChecked ?? false;

        setListRows((prev) =>
          prev.map((item) => {
            if (item.tracker.id === id && item.tracker.type === type) {
              return {
                ...item,
                tracker: {
                  ...item.tracker,
                  isChecked: !item.tracker.isChecked,
                  ...(item.tracker.isStartDateDisabled
                    ? {}
                    : {
                        currentStartDate: item.tracker.isChecked
                          ? null
                          : item.tracker.currentStartDate,
                      }),
                  currentEndDate: item.tracker.isChecked
                    ? null
                    : item.tracker.currentEndDate,
                  isStartDateCritical: false,
                  isEndDateCritical: false,
                  isJustificationCritical: false,
                  currentJustification: '',
                },
              };
            }

            if (isSingleSelect) {
              if (type === AccessTypeMap.entitlement) {
                const targetEntitlementType = (
                  config as { entitlementType: string }
                ).entitlementType;

                return {
                  ...item,
                  tracker: {
                    ...item.tracker,
                    isChecked:
                      (item as EntitlementAccessData[number])
                        .entitlementtype === targetEntitlementType
                        ? false
                        : item.tracker.isChecked,
                  },
                };
              }

              return {
                ...item,
                tracker: {
                  ...item.tracker,
                  isChecked:
                    item.tracker.type === type ? false : item.tracker.isChecked,
                },
              };
            }

            return item;
          })
        );

        // Force-unchecking peers only applies when selecting (not deselecting) in single-select mode.
        const forceUncheckedItems =
          isSingleSelect && !wasChecked
            ? listRowsRef.current.filter((item) => {
                if (item.tracker.id === id && item.tracker.type === type) {
                  return false;
                }

                if (!item.tracker.isChecked) {
                  return false;
                }

                if (type === AccessTypeMap.entitlement) {
                  const targetEntitlementType = (
                    config as { entitlementType: string }
                  ).entitlementType;

                  return (
                    (item as EntitlementAccessData[number]).entitlementtype ===
                    targetEntitlementType
                  );
                }

                return item.tracker.type === type;
              })
            : [];

        if (!clickedItem) {
          return;
        }

        setDelta((prev) => {
          let updatedAdded = [...prev.added];
          let updatedRemoved = [...prev.removed];

          forceUncheckedItems.forEach((forceItem) => {
            updatedAdded = updatedAdded.filter(
              (a) =>
                !(
                  a.tracker.id === forceItem.tracker.id &&
                  a.tracker.type === forceItem.tracker.type
                )
            );

            if (
              !updatedRemoved.some(
                (r) =>
                  r.tracker.id === forceItem.tracker.id &&
                  r.tracker.type === forceItem.tracker.type
              )
            ) {
              updatedRemoved = [...updatedRemoved, forceItem];
            }
          });

          if (!wasChecked) {
            updatedAdded = [
              ...updatedAdded.filter(
                (a) => !(a.tracker.id === id && a.tracker.type === type)
              ),
              {
                ...clickedItem,
                tracker: { ...clickedItem.tracker, isChecked: true },
              },
            ];
            updatedRemoved = updatedRemoved.filter(
              (r) => !(r.tracker.id === id && r.tracker.type === type)
            );
          } else {
            updatedAdded = updatedAdded.filter(
              (a) => !(a.tracker.id === id && a.tracker.type === type)
            );
            updatedRemoved = [
              ...updatedRemoved.filter(
                (r) => !(r.tracker.id === id && r.tracker.type === type)
              ),
              clickedItem,
            ];
          }

          return { added: updatedAdded, removed: updatedRemoved };
        });
      },
      [accessConfig, roleAccessConfig]
    );

    const handleRowClick = useCallback(
      (id: number, type: AccessTypeMap) => {
        if (isFullView) {
          const activeIndex = focusedList.findIndex(
            (e) => e.tracker.type === type && e.tracker.id === id
          );

          if (activeIndex !== -1) {
            setSidePanelContext({
              activeIndex,
              activeItem: listRows[activeIndex],
            });

            setIsSidePanelOpen(true);
          }
        }
      },
      [listRows, isFullView, focusedList]
    );

    const handleOptionClick = useCallback(
      (
        id: number,
        type: AccessTypeMap,
        startDate?: Date | null,
        enddate?: Date | null
      ) => {
        const applyDates = (item: TrackedListItem): TrackedListItem => {
          if (item.tracker.id === id && item.tracker.type === type) {
            return {
              ...item,
              tracker: {
                ...item.tracker,
                currentStartDate: startDate,
                currentEndDate: enddate,
                isStartDateCritical: item.tracker.isStartDateCritical
                  ? false
                  : item.tracker.isStartDateCritical,
                isEndDateCritical: item.tracker.isEndDateCritical
                  ? false
                  : item.tracker.isEndDateCritical,
              },
            };
          }

          return item;
        };

        setListRows((prev) => prev.map(applyDates));
        setActiveRows((prev) => prev.map(applyDates));
        setDelta((prev) => ({
          ...prev,
          added: prev.added.map(applyDates),
        }));
      },
      []
    );

    const handleComment = useCallback(
      (id: number, type: AccessTypeMap, text: string) => {
        const applyComment = (item: TrackedListItem): TrackedListItem => {
          if (item.tracker.id === id && item.tracker.type === type) {
            return {
              ...item,
              tracker: {
                ...item.tracker,
                currentJustification: text,
                isJustificationCritical: item.tracker.isJustificationCritical
                  ? false
                  : item.tracker.isJustificationCritical,
              },
            };
          }

          return item;
        };

        setListRows((prev) => prev.map(applyComment));
        setActiveRows((prev) => prev.map(applyComment));
        setDelta((prev) => ({
          ...prev,
          added: prev.added.map(applyComment),
        }));
      },
      []
    );

    const handleSearch = useCallback((text: string) => {
      setListControls((prev) => ({ ...prev, searchText: text }));
    }, []);

    const handleControls = useCallback(
      (modifiedControls: ListFilters[]) => {
        const accessTypeFilterValue = modifiedControls
          .find((f) => f.value === ACCESS_TYPE_FILTER_VALUE)
          ?.options.find((o) => o.isChecked)?.value;

        if (accessTypeFilterValue === AccessTypeMap.entitlement) {
          const isEntFilter = modifiedControls.find(
            (f) => f.value === ENTITLEMENT_TYPE_FILTER_VALUE
          );

          const newModifiedControls = [...modifiedControls];

          if (!isEntFilter) {
            newModifiedControls.push({
              ...ENTITLEMENT_TYPE_FILTER,
              options: [
                ...Object.values(
                  accessConfig?.[AccessTypeMap.entitlement] ?? {}
                ).map((entType) => ({
                  label: entType.entitlementTypeDisplayName,
                  value: String(entType.entitlementTypeDisplayName),
                })),
              ],
            });
          }

          setListControls((prev) => ({
            ...prev,
            controls: newModifiedControls,
          }));

          return;
        }

        setListControls((prev) => ({
          ...prev,
          controls: modifiedControls.filter(
            (f) => f.value === ACCESS_TYPE_FILTER_VALUE
          ),
        }));
      },
      [accessConfig]
    );

    const handleValidate = useCallback(() => {
      const inValidRows = activeRows.filter((item) => {
        const {
          isStartDate,
          isEndDate,
          isStartDateMandatory,
          isEndDateMandatory,
          currentStartDate,
          currentEndDate,
          isJustificationRequired,
          currentJustification,
        } = item.tracker;
        // Date validations

        if (isStartDate && isStartDateMandatory && currentStartDate === null) {
          return true;
        }

        if (isEndDate && isEndDateMandatory && currentEndDate === null) {
          return true;
        }

        if (
          isJustificationRequired &&
          currentJustification.trim().length < MIN_COMMENT_LENGTH
        ) {
          return true;
        }

        return false;
      });

      if (inValidRows.length === 0) {
        return true;
      }

      if (assignType === 1) {
        setAssignType(2);
      }

      const modifiedInValidRows = inValidRows.map((item) => item.tracker);

      setActiveRows((prev) =>
        prev.map((item) => {
          const isTargetItem = modifiedInValidRows.some(
            (obj) =>
              obj.id === item.tracker.id && obj.type === item.tracker.type
          );

          if (isTargetItem) {
            const {
              isStartDate,
              isEndDate,
              isStartDateMandatory,
              isEndDateMandatory,
              currentStartDate,
              currentEndDate,
              isJustificationRequired,
              currentJustification,
            } = item.tracker;
            const isStartDateCritical = Boolean(
              isStartDate && isStartDateMandatory && currentStartDate === null
            );
            const isEndDateCritical = Boolean(
              isEndDate && isEndDateMandatory && currentEndDate === null
            );
            const isJustificationCritical = Boolean(
              isJustificationRequired &&
                currentJustification.trim().length < MIN_COMMENT_LENGTH
            );

            return {
              ...item,
              tracker: {
                ...item.tracker,
                isStartDateCritical,
                isEndDateCritical,
                isJustificationCritical,
              },
            };
          }

          return item;
        })
      );

      return false;
    }, [activeRows, assignType]);

    const handleIsEmpty = useCallback(() => {
      if (!listState.isListError && !listState.isListLoading) {
        return true;
      }

      return false;
    }, [listState.isListError, listState.isListLoading]);

    useImperativeHandle(
      ref,
      () => ({ validate: handleValidate, isEmpty: handleIsEmpty }),
      [handleValidate, handleIsEmpty]
    );

    // SidePanel utils
    const {
      headerTitle,
      headerDescription,
      headerIcon,
      isPreviousDisabled,
      isNextDisabled,
    } = useMemo(() => {
      const payload = {
        headerTitle: '',
        headerDescription: '',
        headerIcon: <ApplicationIcon icon='Access' size='xLarge' />,
        isPreviousDisabled: true,
        isNextDisabled: true,
      };

      // Swipe buttons state
      if (focusedList.length > 1) {
        if (sidePanelContext.activeIndex === 0) {
          payload.isPreviousDisabled = true;
          payload.isNextDisabled = false;
        }

        if (sidePanelContext.activeIndex === focusedList.length - 1) {
          payload.isPreviousDisabled = false;
          payload.isNextDisabled = true;
        }

        if (
          sidePanelContext.activeIndex !== null &&
          sidePanelContext.activeIndex !== -1 &&
          sidePanelContext.activeIndex > 0 &&
          sidePanelContext.activeIndex < focusedList.length - 1
        ) {
          payload.isPreviousDisabled = false;
          payload.isNextDisabled = false;
        }
      }

      // Header contents
      if (sidePanelContext.activeItem) {
        const item = sidePanelContext.activeItem;

        payload.headerTitle =
          item.displayname ??
          (item.tracker.type === AccessTypeMap.entitlement
            ? (item as EntitlementAccessData[number]).entitlement_value
            : (item as EnterpriseRoleAccessData[number]).rolename);
      }

      if (
        sidePanelContext.activeItem?.tracker.type === AccessTypeMap.entitlement
      ) {
        payload.headerDescription = 'Entitlement';
        payload.headerIcon = (
          <ApplicationIcon icon='Entitlement' size='xLarge' />
        );
      }

      if (
        sidePanelContext.activeItem?.tracker.type ===
        AccessTypeMap.applicationRole
      ) {
        payload.headerDescription = 'Application Role';
        payload.headerIcon = (
          <ApplicationIcon icon='ApplicationRole' size='xLarge' />
        );
      }

      if (
        sidePanelContext.activeItem?.tracker.type ===
        AccessTypeMap.enterpriseRole
      ) {
        payload.headerDescription = 'Enterprise Role';
        payload.headerIcon = (
          <ApplicationIcon icon='Enterprise Role' size='xLarge' />
        );
      }

      return payload;
    }, [sidePanelContext, focusedList]);

    const handlePrevious = useCallback(() => {
      if (
        sidePanelContext.activeIndex !== null &&
        sidePanelContext.activeIndex !== -1
      ) {
        const newIndex = sidePanelContext.activeIndex - 1;

        if (newIndex >= 0) {
          setSidePanelTab('1');
          setSidePanelContext({
            activeIndex: newIndex,
            activeItem: focusedList[newIndex],
          });
        }
      }
    }, [sidePanelContext, focusedList]);

    const handleNext = useCallback(() => {
      if (
        sidePanelContext.activeIndex !== null &&
        sidePanelContext.activeIndex !== -1
      ) {
        const newIndex = sidePanelContext.activeIndex + 1;

        if (newIndex < focusedList.length) {
          setSidePanelTab('1');
          setSidePanelContext({
            activeIndex: newIndex,
            activeItem: focusedList[newIndex],
          });
        }
      }
    }, [sidePanelContext, focusedList]);

    const renderSummary = useCallback((activeItem: TrackedListItem) => {
      const { tracker, ...body } = activeItem;

      if (tracker.type === AccessTypeMap.entitlement) {
        const typebody = body as EntitlementAccessData[number];

        return (
          <>
            <Box
              tag='div'
              className='AccessModal-SidePanelBody-listItem--isEntitlement'>
              <Typography kind='h4' color={CF_NEU_BLD}>
                <FormattedMessage
                  id='3T09+Q'
                  defaultMessage='Description'
                  description='Description'
                />
              </Typography>
              <Typography kind='body2' color={CF_SEC_BLD} lineLimit={1}>
                {typebody.description ?? (
                  <FormattedMessage
                    id='YoUJI1'
                    defaultMessage='-'
                    description='Hyphen'
                  />
                )}
              </Typography>
            </Box>
            <Box
              tag='div'
              className='AccessModal-SidePanelBody-listItem--isEntitlement'>
              <Typography kind='h4' color={CF_NEU_BLD}>
                <FormattedMessage
                  id='88ZOyq'
                  defaultMessage='Entitlement Type'
                  description='Header for entitlement type in access details side panel'
                />
              </Typography>
              <Typography kind='body2' color={CF_SEC_BLD} lineLimit={1}>
                {typebody.entitlementtype}
              </Typography>
            </Box>
            <Box
              tag='div'
              className='AccessModal-SidePanelBody-listItem--isEntitlement'>
              <Typography kind='h4' color={CF_NEU_BLD}>
                <FormattedMessage
                  id='MDQje4'
                  defaultMessage='SOX Criticality'
                  description='Header for SOX criticality in access details side panel'
                />
              </Typography>
              <Typography kind='body2' color={CF_SEC_BLD} lineLimit={1}>
                <SoxCritical value={typebody.soxcritical} isIcon={false} />
              </Typography>
            </Box>
          </>
        );
      }

      return (
        <>
          <Box tag='div' className='AccessModal-SidePanelBody-listItem--isRole'>
            <Typography kind='h4' color={CF_NEU_BLD}>
              <FormattedMessage
                id='3T09+Q'
                defaultMessage='Description'
                description='Description'
              />
            </Typography>
            <Typography kind='body2' color={CF_SEC_BLD}>
              {body.description ?? (
                <FormattedMessage
                  id='YoUJI1'
                  defaultMessage='-'
                  description='Hyphen'
                />
              )}
            </Typography>
          </Box>
          <Box tag='div' className='AccessModal-SidePanelBody-listItem--isRole'>
            <Typography kind='h4' color={CF_NEU_BLD}>
              <FormattedMessage
                id='MDQje4'
                defaultMessage='SOX Criticality'
                description='Header for SOX criticality in access details side panel'
              />
            </Typography>
            <Typography kind='body2' color={CF_SEC_BLD} lineLimit={1}>
              <SoxCritical value={body.soxcritical} isIcon={false} />
            </Typography>
          </Box>
        </>
      );
    }, []);

    const renderChildren = useCallback(() => {
      if (sidePanelState.isAccessLoading) {
        return (
          <Box tag='div' className='AccessModal-SidePanelLayout-contentLoader'>
            <Loader kind='dots' size='small' format='inline' color='brand' />
          </Box>
        );
      }

      if (sidePanelState.isAccessError) {
        return (
          <Box tag='div' className='AccessModal-SidePanelLayout-contentLoader'>
            <Box className='ListViewer-errorModal'>
              <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
              <Typography kind='body2' color={CF_CRT_MED}>
                <FormattedMessage
                  id='apz7lc'
                  defaultMessage='System encountered an error. Please contact the Support Administrator'
                  description='System encountered an error. Please contact the Support Administrator'
                />
              </Typography>
            </Box>
          </Box>
        );
      }

      if (sidePanelState.children.length === 0) {
        return (
          <Box tag='div' className='AccessModal-SidePanelLayout-contentLoader'>
            <Box className='ListViewer-errorModal'>
              <Icon kind='AlertInfo' size='large' color={CF_NEU_BLD} />
              <Typography kind='body2'>
                <FormattedMessage
                  id='MKB1Ak'
                  defaultMessage='No children available'
                  description='Message when there are no child entities for an access in access details side panel'
                />
              </Typography>
            </Box>
          </Box>
        );
      }

      return (
        <Box
          tag='div'
          className='AccessModal-SidePanelLayout-contentListContainer'>
          {sidePanelState.children.map((c) => (
            <Box
              key={c.id}
              tag='div'
              className='AccessModal-SidePanelLayout-contentListItem'>
              <Icon kind='Entitlement' color={CF_NEU_BLD} />
              <Typography kind='body2' color={CF_SEC_BLD} lineLimit={1}>
                {c.displayName ?? c.name ?? (
                  <FormattedMessage
                    id='YoUJI1'
                    defaultMessage='-'
                    description='Hyphen'
                  />
                )}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }, [sidePanelState]);

    const renderSidePanelBody = useCallback(() => {
      if (sidePanelContext.activeItem) {
        return (
          <>
            <TabGroup
              value={sidePanelTab}
              onChange={(value: string) => setSidePanelTab(value)}
              size='medium'
              className='AccessModal-SidePanelLayout-tabGroup'>
              <TabItem
                label='Summary'
                value='1'
                icon={<Icon kind='Document' size='medium' />}
              />
              {sidePanelContext.activeItem.tracker.type !==
                AccessTypeMap.applicationRole && (
                <TabItem
                  label='Children'
                  value='2'
                  icon={<Icon kind='Entitlement' size='medium' />}
                />
              )}
            </TabGroup>
            <TabContent
              value={sidePanelTab}
              tabValue='1'
              className='AccessModal-SidePanelLayout-tabContent'>
              {renderSummary(sidePanelContext.activeItem)}
            </TabContent>
            <TabContent
              value={sidePanelTab}
              tabValue='2'
              className='AccessModal-SidePanelLayout-tabContent'>
              {renderChildren()}
            </TabContent>
          </>
        );
      }

      return null;
    }, [sidePanelContext, sidePanelTab, renderSummary, renderChildren]);

    const renderBody = useCallback(() => {
      if (isFullView) {
        return focusedList.map((item) => (
          <AccessModalListItem
            key={`${item.tracker.id}-${item.tracker.type}`}
            isAssignedList={assignType === 2}
            isFullView={isFullView}
            assignedCount={activeRows.length}
            isDisabled={listState.isListLoading}
            listItem={item}
            handleClick={handleClick}
            handleRowClick={handleRowClick}
            handleOptionClick={handleOptionClick}
            handleComment={handleComment}
          />
        ));
      }

      const groupedData = groupByEntitlementType(listRows);

      return Object.entries(groupedData).map(([key, value], index) => (
        <AccessModalSegment
          key={key}
          isFullView={isFullView}
          assignedCount={activeRows.length}
          groupName={key as AccessTypeMap}
          isDisabled={listState.isListLoading}
          isLast={index === Object.keys(groupedData).length - 1}
          list={value.slice(0, 2)}
          handleClick={handleClick}
          handleRowClick={handleRowClick}
        />
      ));
    }, [
      activeRows,
      assignType,
      listRows,
      focusedList,
      handleClick,
      handleOptionClick,
      handleRowClick,
      handleComment,
      isFullView,
      listState.isListLoading,
    ]);

    return (
      <>
        <Box
          tag='div'
          className='AccessModal'
          dataTestId={testIds.LIST_VIEWER_ACCESS_MODAL}>
          {isFullView && (
            <>
              <Box tag='div'>
                <Typography kind='h2' color={CF_SEC_BLD}>
                  <FormattedMessage
                    id='muN8x9'
                    defaultMessage='Choose Access'
                    description='Header for access selection modal'
                  />
                </Typography>
              </Box>
              <Box tag='div' className='AccessModal-togglers'>
                <ToggleButtonGroup
                  selection={assignType}
                  setSelection={setAssignType}
                  isDisabled={listState.isListLoading}>
                  <ToggleButton
                    label='Available'
                    size='medium'
                    isDisabled={listState.isListLoading}
                  />
                  <ToggleButton
                    label='Assigned'
                    size='medium'
                    isDisabled={listState.isListLoading}
                  />
                </ToggleButtonGroup>
              </Box>
              <Box tag='div' className='AccessModal-controls'>
                <ListControls
                  controls={listControls.controls}
                  isDisabled={listState.isListLoading || assignType === 2}
                  setSearchText={handleSearch}
                  handleControls={handleControls}
                />
                {APPLICATION_WORKFLOW_KEYS.includes(workflowType) && (
                  <ToggleButtonGroup
                    selection={recommendedMode}
                    setSelection={setRecommendedMode}
                    isDisabled={listState.isListLoading || assignType === 2}>
                    <ToggleButton
                      label='All'
                      size='medium'
                      isDisabled={listState.isListLoading || assignType === 2}
                    />
                    <ToggleButton
                      label='Recommended'
                      size='medium'
                      isDisabled={listState.isListLoading || assignType === 2}
                    />
                  </ToggleButtonGroup>
                )}
              </Box>
            </>
          )}
          <Box tag='div' className='AccessModal-bodyWrapper'>
            {renderBody()}
            {listState.isListError && (
              <Box className='ListViewer-errorModal'>
                <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
                <Typography kind='body2' color={CF_CRT_MED}>
                  <FormattedMessage
                    id='apz7lc'
                    defaultMessage='System encountered an error. Please contact the Support Administrator'
                    description='System encountered an error. Please contact the Support Administrator'
                  />
                </Typography>
              </Box>
            )}
            {/* Observer Element */}
            {isFullView && !listState.isListError && !listState.isListLoading
              ? listObserverElement
              : null}
            {/* Loading Skeleton */}
            {!listState.isListError && listState.isListLoading && (
              <Box className='ListViewer-loadingModal'>
                <ListSkeleton count={3} />
              </Box>
            )}
            {!listState.isListError &&
              !listState.isListLoading &&
              focusedList.length === 0 && (
                <Box tag='div' className='AccessModal-illustrationWrapper'>
                  <Illustration kind='Empty' />
                  <Typography kind='h1'>
                    {assignType === 1 &&
                      (isFilterApplied ? (
                        <FormattedMessage
                          id='C0QjhC'
                          defaultMessage='No Results Found!'
                          description='No Results Found!'
                        />
                      ) : (
                        <FormattedMessage
                          id='1mOl1P'
                          defaultMessage='No selections available, please proceed to next step!'
                          description='Message when there are no access available for selection in access selection modal'
                        />
                      ))}
                    {assignType === 2 && (
                      <FormattedMessage
                        id='vw9k/g'
                        defaultMessage='No selections available!'
                        description='Message when there are no access selected'
                      />
                    )}
                  </Typography>
                </Box>
              )}
          </Box>
        </Box>
        <ListSidePanel
          isOpen={isSidePanelOpen}
          onClose={() => {
            setIsSidePanelOpen(false);
            setSidePanelContext(DEFAULT_SIDEPANEL_STATE);
            setSidePanelTab('1');
            setSidePanelState({
              isAccessLoading: true,
              isAccessError: false,
              children: [] as ChildAccess[],
            });
          }}
          header={{
            title: headerTitle,
            description: headerDescription,
            headerIcon,
          }}
          layout={renderSidePanelBody()}
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          isPreviousDisabled={isPreviousDisabled}
          isNextDisabled={isNextDisabled}
          className='AccessModal-SidePanel'
          classNames={{
            headerClassName: 'AccessModal-SidePanelHeader',
            layoutClassName: 'AccessModal-SidePanelLayout',
          }}
        />
      </>
    );
  }
);

AccessModal.defaultProps = {
  retentions: undefined,
};

export default memo(AccessModal);
